import React, { useState, useContext } from 'react'
import { Typography, TextField, Button, Box, Container, Alert, CircularProgress } from '@mui/material'
import { useMutation } from '@apollo/client'

import loginMutation from '../../graphql/mutation/login'
import AuthContext from '../../contexts/AuthContext'

const LoginPage = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const { setToken } = useContext(AuthContext)
  const [login] = useMutation(loginMutation)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const result = await login({
        variables: {
          email,
          password,
        },
      })
      if (result.data && result.data.login) {
        setToken(result.data.login)
      }
    } catch (err) {
      console.error('Login error:', err)
      // Extract error message from backend
      const errorMessage =
        err.graphQLErrors?.[0]?.message ||
        err.networkError?.message ||
        err.message ||
        'Login failed. Please try again.'
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box
      component="main"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.default',
        px: { xs: 2, sm: 3, md: 4 },
        py: { xs: 3, sm: 4, md: 5 },
      }}>
      <Container
        maxWidth="xs"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          width: '100%',
          maxWidth: { xs: '100%', sm: 400, md: 450 },
        }}>
        {/* Title: "Sign in" - Left aligned */}
        <Typography
          component="h1"
          variant="h4"
          sx={{
            fontWeight: 600,
            mb: { xs: 1.25, sm: 1.5, md: 1.75 },
            color: 'text.primary',
            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' },
            width: '100%',
            textAlign: 'left',
          }}>
          Sign in
        </Typography>

        {/* Subtitle: "Thank you for continuously supporting Racepack" - Left aligned */}
        <Typography
          variant="body1"
          component="p"
          sx={{
            mb: { xs: 3, sm: 3.5, md: 4 },
            textAlign: 'left',
            color: 'text.primary',
            fontSize: { xs: '0.875rem', sm: '0.9rem', md: '0.95rem' },
            lineHeight: 1.6,
            fontWeight: 400,
            width: '100%',
            '& strong': {
              fontWeight: 600,
              color: 'text.primary',
            },
          }}>
          Thank you for continuously supporting <strong>Racepack</strong>
        </Typography>

        {/* Form */}
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 2, sm: 2.25, md: 2.5 },
          }}>
          {/* Error Alert */}
          {error && (
            <Alert
              severity="error"
              onClose={() => setError('')}
              sx={{
                borderRadius: 1,
                fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
              }}>
              {error}
            </Alert>
          )}

          {/* Username Input Field */}
          <TextField
            autoFocus
            id="email"
            name="email"
            label="Username"
            type="text"
            placeholder="Username"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value)
              if (error) setError('')
            }}
            error={!!error}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'background.paper',
                borderRadius: 1,
                fontSize: { xs: '0.875rem', sm: '0.9rem', md: '1rem' },
                '& fieldset': {
                  borderColor: (theme) =>
                    theme.palette.mode === 'light'
                      ? 'rgba(0, 0, 0, 0.23)'
                      : 'rgba(255, 255, 255, 0.23)',
                },
                '&:hover fieldset': {
                  borderColor: (theme) =>
                    theme.palette.mode === 'light'
                      ? 'rgba(0, 0, 0, 0.87)'
                      : 'rgba(255, 255, 255, 0.87)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                  borderWidth: '1px',
                },
              },
              '& .MuiInputLabel-root': {
                color: 'text.primary',
                fontWeight: 400,
                fontSize: { xs: '0.875rem', sm: '0.9rem', md: '1rem' },
                '&.Mui-focused': {
                  color: 'primary.main',
                },
              },
              '& .MuiOutlinedInput-input': {
                color: 'text.primary',
                py: { xs: 1.25, sm: 1.375, md: 1.5 },
                '&::placeholder': {
                  color: (theme) =>
                    theme.palette.mode === 'light'
                      ? 'rgba(0, 0, 0, 0.5)'
                      : 'rgba(255, 255, 255, 0.5)',
                  opacity: 1,
                },
              },
            }}
          />

          {/* Password Input Field */}
          <TextField
            id="password"
            name="password"
            label="Password"
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value)
              if (error) setError('')
            }}
            error={!!error}
            required
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'background.paper',
                borderRadius: 1,
                fontSize: { xs: '0.875rem', sm: '0.9rem', md: '1rem' },
                '& fieldset': {
                  borderColor: (theme) =>
                    theme.palette.mode === 'light'
                      ? 'rgba(0, 0, 0, 0.23)'
                      : 'rgba(255, 255, 255, 0.23)',
                },
                '&:hover fieldset': {
                  borderColor: (theme) =>
                    theme.palette.mode === 'light'
                      ? 'rgba(0, 0, 0, 0.87)'
                      : 'rgba(255, 255, 255, 0.87)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                  borderWidth: '1px',
                },
              },
              '& .MuiInputLabel-root': {
                color: 'text.primary',
                fontWeight: 400,
                fontSize: { xs: '0.875rem', sm: '0.9rem', md: '1rem' },
                '&.Mui-focused': {
                  color: 'primary.main',
                },
              },
              '& .MuiOutlinedInput-input': {
                color: 'text.primary',
                py: { xs: 1.25, sm: 1.375, md: 1.5 },
                '&::placeholder': {
                  color: (theme) =>
                    theme.palette.mode === 'light'
                      ? 'rgba(0, 0, 0, 0.5)'
                      : 'rgba(255, 255, 255, 0.5)',
                  opacity: 1,
                },
              },
            }}
          />

          {/* Login Button */}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            disabled={loading}
            sx={{
              mt: { xs: 0.5, sm: 0.75, md: 1 },
              py: { xs: 1.25, sm: 1.375, md: 1.5 },
              borderRadius: 1,
              fontWeight: 600,
              fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' },
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': {
                boxShadow: 'none',
                backgroundColor: 'primary.dark',
              },
              '&:disabled': {
                opacity: 0.6,
              },
            }}>
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} sx={{ color: 'inherit' }} />
                <span>Logging in...</span>
              </Box>
            ) : (
              'Login'
            )}
          </Button>
        </Box>
      </Container>
    </Box>
  )
}

export default LoginPage
